import apiClient from './config';

const SUPERADMIN_BASE = '/api/superadmin';

export interface PagedResponse<T>
{
    items: T[];
    total: number;
    page: number;
    page_size: number;
}

export interface SuperAdminFeedbackSubmission
{
    id: number;
    category: 'feedback' | 'bug' | 'suggestion';
    subject: string;
    message: string;
    page_url?: string | null;
    user_id?: number | null;
    user_email: string;
    user_name?: string | null;
    user_role: string;
    organization_id?: number | null;
    organization_name?: string | null;
    created_at: string | null;
}

export interface SuperAdminFeedbackListParams
{
    page?: number;
    page_size?: number;
    category?: 'all' | 'feedback' | 'bug' | 'suggestion';
    search?: string;
    organization_id?: number;
}

export interface UserOrganizationMembership
{
    id: number;
    name: string;
    role: string;
    team_id?: number | null;
}

export interface SuperAdminUser
{
    id: number;
    email: string;
    username: string;
    full_name?: string | null;
    is_active: boolean;
    is_admin: boolean;
    is_superuser: boolean;
    has_temporary_password: boolean;
    organization_id?: number | null;
    organization_name?: string | null;
    organizations?: UserOrganizationMembership[];
    team_id?: number | null;
    team_name?: string | null;
    created_at: string;
}

export interface SuperAdminUserListParams
{
    page?: number;
    page_size?: number;
    search?: string;
    organization_id?: number;
    team_id?: number;
    is_admin?: boolean;
    is_superuser?: boolean;
    is_active?: boolean;
}

export interface SuperAdminCreateUserPayload
{
    email: string;
    username: string;
    full_name?: string;
    password: string;
    is_admin?: boolean;
    is_superuser?: boolean;
    organization_id?: number;
    team_id?: number;
}

export interface SuperAdminUpdateUserPayload
{
    email?: string;
    username?: string;
    full_name?: string;
    is_active?: boolean;
    is_admin?: boolean;
    is_superuser?: boolean;
    organization_id?: number;
    team_id?: number;
}

export interface SuperAdminOrganization
{
    id: number;
    name: string;
    organization_type?: string | null;
    organization_size?: string | null;
    created_by_user_id?: number | null;
    created_at: string;
    user_count: number;
    team_count: number;
}

export interface SuperAdminOrganizationListParams
{
    page?: number;
    page_size?: number;
    search?: string;
}

export interface SuperAdminOrganizationUser
{
    id: number;
    email: string;
    username: string;
    full_name?: string | null;
    is_admin: boolean;
    is_active: boolean;
    team_id?: number | null;
    created_at: string;
}

export interface AddUserToOrganizationPayload
{
    user_id: number;
    role?: 'user' | 'admin';
    team_id?: number;
}

export interface SuperAdminTeam
{
    id: number;
    name: string;
    description?: string | null;
    organization_id: number;
    organization_name?: string | null;
    created_by_user_id?: number | null;
    created_at: string;
    member_count: number;
}

export interface SuperAdminTeamListParams
{
    page?: number;
    page_size?: number;
    search?: string;
    organization_id?: number;
}

export interface SuperAdminTeamMember
{
    id: number;
    email: string;
    username: string;
    full_name?: string | null;
    is_admin: boolean;
    is_active: boolean;
    organization_id?: number | null;
}

export interface CreateTeamPayload
{
    name: string;
    description?: string;
    organization_id: number;
}

export interface UpdateTeamPayload
{
    name?: string;
    description?: string;
    organization_id?: number;
}

export interface PlatformOverviewStats
{
    users: {
        total: number;
        active: number;
        admins: number;
        superusers: number;
    };
    organizations: {
        total: number;
    };
    teams: {
        total: number;
    };
    jobs: {
        total: number;
        by_status: Record<string, number>;
    };
    token_usage: {
        prompt_tokens: number;
        completion_tokens: number;
        total_tokens: number;
    };
    billing: {
        total_charged_usd: number;
        customer_charge_usd?: number;
        provider_cost_usd?: number;
        profit_usd?: number;
        payment_total_usd?: number;
        llm_usage_request_count?: number;
        transaction_count?: number;
    };
    window_days?: number | null;
}

export interface OrganizationStats
{
    organization: {
        id: number;
        name: string;
    };
    users: {
        total: number;
        admins: number;
    };
    teams: {
        total: number;
        details: Array<{
            team_id: number;
            team_name: string;
            member_count: number;
        }>;
    };
    jobs: {
        total: number;
        by_status: Record<string, number>;
    };
    token_usage: {
        prompt_tokens: number;
        completion_tokens: number;
        total_tokens: number;
    };
    per_user_token_usage: Array<{
        user_id: number;
        total_tokens: number;
    }>;
    billing: {
        stripe_customer_id?: string | null;
        has_payment_method: boolean;
        price_per_1k_tokens?: number | null;
        free_token_quota: number;
        total_charged_usd: number;
        customer_charge_usd?: number;
        provider_cost_usd?: number;
        profit_usd?: number;
        payment_total_usd?: number;
        llm_usage_request_count?: number;
        transaction_count: number;
    };
    window_days?: number | null;
}

export interface TeamStats
{
    team: {
        id: number;
        name: string;
        organization_id: number;
    };
    members: {
        total: number;
    };
    jobs: {
        total: number;
        by_status: Record<string, number>;
    };
    token_usage: {
        prompt_tokens: number;
        completion_tokens: number;
        total_tokens: number;
    };
    per_member_token_usage: Array<{
        user_id: number;
        total_tokens: number;
    }>;
    window_days?: number | null;
}

export async function listSuperAdminFeedback(
    params?: SuperAdminFeedbackListParams,
): Promise<PagedResponse<SuperAdminFeedbackSubmission>>
{
    const { data } = await apiClient.get<PagedResponse<SuperAdminFeedbackSubmission>>(
        `${SUPERADMIN_BASE}/feedback`,
        { params },
    );
    return data;
}

export interface UserStats
{
    user: {
        id: number;
        email: string;
        username: string;
        is_admin: boolean;
        is_superuser: boolean;
        organization_id?: number | null;
        team_id?: number | null;
    };
    token_usage: {
        prompt_tokens: number;
        completion_tokens: number;
        total_tokens: number;
    };
    per_model_usage: Array<{
        model: string;
        total_tokens: number;
    }>;
    jobs_in_org: number;
    billing?: {
        stripe_customer_id?: string | null;
        has_payment_method: boolean;
        price_per_1k_tokens?: number | null;
        free_token_quota: number;
        total_charged_usd: number;
        transaction_count: number;
    } | null;
    window_days?: number | null;
}

export interface SuperAdminJob
{
    id: number;
    generation_kind?: string | null;
    artifact_type?: string | null;
    status?: string | null;
    progress: number;
    organization_id?: number | null;
    organization_name?: string | null;
    project_id?: number | null;
    repository_id?: number | null;
    error_message?: string | null;
    started_at?: string | null;
    completed_at?: string | null;
    created_at?: string | null;
}

export interface SuperAdminJobsParams
{
    page?: number;
    page_size?: number;
    status?: string;
    generation_kind?: string;
    organization_id?: number;
    project_id?: number;
}

export interface JobSummary
{
    total: number;
    by_status: Record<string, number>;
}

export async function listSuperAdminUsers(params: SuperAdminUserListParams)
{
    const { data } = await apiClient.get<PagedResponse<SuperAdminUser>>(`${SUPERADMIN_BASE}/users`, { params });
    return data;
}

export async function createSuperAdminUser(payload: SuperAdminCreateUserPayload)
{
    const { data } = await apiClient.post<SuperAdminUser>(`${SUPERADMIN_BASE}/users`, payload);
    return data;
}

export async function getSuperAdminUser(userId: number)
{
    const { data } = await apiClient.get<SuperAdminUser>(`${SUPERADMIN_BASE}/users/${userId}`);
    return data;
}

export async function updateSuperAdminUser(userId: number, payload: SuperAdminUpdateUserPayload)
{
    const { data } = await apiClient.put<SuperAdminUser>(`${SUPERADMIN_BASE}/users/${userId}`, payload);
    return data;
}

export async function deleteSuperAdminUser(userId: number)
{
    await apiClient.delete(`${SUPERADMIN_BASE}/users/${userId}`);
}

export async function assignUserToOrganization(userId: number, organizationId: number, teamId?: number)
{
    const { data } = await apiClient.post<SuperAdminUser>(`${SUPERADMIN_BASE}/users/${userId}/assign-org`, {
        organization_id: organizationId,
        team_id: teamId,
    });
    return data;
}

export async function removeUserFromOrganization(userId: number, organizationId: number)
{
    const { data } = await apiClient.delete<SuperAdminUser>(`${SUPERADMIN_BASE}/users/${userId}/remove-from-org`, {
        params: { org_id: organizationId },
    });
    return data;
}

export async function resetSuperAdminUserPassword(userId: number)
{
    const { data } = await apiClient.post<{ temporary_password: string }>(`${SUPERADMIN_BASE}/users/${userId}/reset-password`);
    return data;
}

export async function promoteSuperAdminUser(userId: number)
{
    const { data } = await apiClient.post<SuperAdminUser>(`${SUPERADMIN_BASE}/users/${userId}/promote-admin`);
    return data;
}

export async function demoteSuperAdminUser(userId: number)
{
    const { data } = await apiClient.post<SuperAdminUser>(`${SUPERADMIN_BASE}/users/${userId}/demote-admin`);
    return data;
}

export async function listSuperAdminOrganizations(params: SuperAdminOrganizationListParams)
{
    const { data } = await apiClient.get<PagedResponse<SuperAdminOrganization>>(`${SUPERADMIN_BASE}/organizations`, { params });
    return data;
}

export async function createSuperAdminOrganization(payload: { name: string; organization_type?: string; organization_size?: string })
{
    const { data } = await apiClient.post<SuperAdminOrganization>(`${SUPERADMIN_BASE}/organizations`, payload);
    return data;
}

export async function getSuperAdminOrganization(orgId: number)
{
    const { data } = await apiClient.get<SuperAdminOrganization>(`${SUPERADMIN_BASE}/organizations/${orgId}`);
    return data;
}

export async function updateSuperAdminOrganization(orgId: number, payload: { name?: string; organization_type?: string; organization_size?: string })
{
    const { data } = await apiClient.put<SuperAdminOrganization>(`${SUPERADMIN_BASE}/organizations/${orgId}`, payload);
    return data;
}

export async function deleteSuperAdminOrganization(orgId: number)
{
    await apiClient.delete(`${SUPERADMIN_BASE}/organizations/${orgId}`);
}

export async function listOrganizationUsers(orgId: number, page = 1, pageSize = 25)
{
    const { data } = await apiClient.get<PagedResponse<SuperAdminOrganizationUser>>(`${SUPERADMIN_BASE}/organizations/${orgId}/users`, {
        params: { page, page_size: pageSize },
    });
    return data;
}

export async function addUserToOrganization(orgId: number, payload: AddUserToOrganizationPayload)
{
    const { data } = await apiClient.post<{ detail: string }>(`${SUPERADMIN_BASE}/organizations/${orgId}/users`, payload);
    return data;
}

export async function removeOrganizationUser(orgId: number, userId: number)
{
    await apiClient.delete(`${SUPERADMIN_BASE}/organizations/${orgId}/users/${userId}`);
}

export async function listSuperAdminTeams(params: SuperAdminTeamListParams)
{
    const { data } = await apiClient.get<PagedResponse<SuperAdminTeam>>(`${SUPERADMIN_BASE}/teams`, { params });
    return data;
}

export async function createSuperAdminTeam(payload: CreateTeamPayload)
{
    const { data } = await apiClient.post<SuperAdminTeam>(`${SUPERADMIN_BASE}/teams`, payload);
    return data;
}

export async function getSuperAdminTeam(teamId: number)
{
    const { data } = await apiClient.get<SuperAdminTeam>(`${SUPERADMIN_BASE}/teams/${teamId}`);
    return data;
}

export async function updateSuperAdminTeam(teamId: number, payload: UpdateTeamPayload)
{
    const { data } = await apiClient.put<SuperAdminTeam>(`${SUPERADMIN_BASE}/teams/${teamId}`, payload);
    return data;
}

export async function deleteSuperAdminTeam(teamId: number)
{
    await apiClient.delete(`${SUPERADMIN_BASE}/teams/${teamId}`);
}

export async function listTeamMembers(teamId: number)
{
    const { data } = await apiClient.get<SuperAdminTeamMember[]>(`${SUPERADMIN_BASE}/teams/${teamId}/members`);
    return data;
}

export async function addTeamMember(teamId: number, userId: number)
{
    const { data } = await apiClient.post<{ detail: string }>(`${SUPERADMIN_BASE}/teams/${teamId}/members/${userId}`);
    return data;
}

export async function removeTeamMember(teamId: number, userId: number)
{
    await apiClient.delete(`${SUPERADMIN_BASE}/teams/${teamId}/members/${userId}`);
}

export async function getPlatformOverview(days?: number)
{
    const { data } = await apiClient.get<PlatformOverviewStats>(`${SUPERADMIN_BASE}/stats/overview`, {
        params: days ? { days } : undefined,
    });
    return data;
}

export async function getOrganizationStats(orgId: number, days?: number)
{
    const { data } = await apiClient.get<OrganizationStats>(`${SUPERADMIN_BASE}/stats/organizations/${orgId}`, {
        params: days ? { days } : undefined,
    });
    return data;
}

export async function getTeamStats(teamId: number, days?: number)
{
    const { data } = await apiClient.get<TeamStats>(`${SUPERADMIN_BASE}/stats/teams/${teamId}`, {
        params: days ? { days } : undefined,
    });
    return data;
}

export async function getUserStats(userId: number, days?: number)
{
    const { data } = await apiClient.get<UserStats>(`${SUPERADMIN_BASE}/stats/users/${userId}`, {
        params: days ? { days } : undefined,
    });
    return data;
}

export async function listSuperAdminJobs(params: SuperAdminJobsParams)
{
    const { data } = await apiClient.get<PagedResponse<SuperAdminJob>>(`${SUPERADMIN_BASE}/jobs`, { params });
    return data;
}

export async function getJobsSummary(organizationId?: number)
{
    const { data } = await apiClient.get<JobSummary>(`${SUPERADMIN_BASE}/jobs/summary`, {
        params: organizationId ? { organization_id: organizationId } : undefined,
    });
    return data;
}

export async function getSuperAdminJob(jobId: number)
{
    const { data } = await apiClient.get<SuperAdminJob>(`${SUPERADMIN_BASE}/jobs/${jobId}`);
    return data;
}

export async function deleteSuperAdminJob(jobId: number)
{
    await apiClient.delete(`${SUPERADMIN_BASE}/jobs/${jobId}`);
}

export async function cancelSuperAdminJob(jobId: number)
{
    const { data } = await apiClient.post<{ detail: string }>(`${SUPERADMIN_BASE}/jobs/${jobId}/cancel`);
    return data;
}

// ---------------------------------------------------------------------------
// Feature 11 — dynamic analytics series
// ---------------------------------------------------------------------------

export interface ModelUsagePoint
{
    model: string;
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    request_count: number;
}

export interface ModelUsageDistribution
{
    organization_id?: number | null;
    window_days?: number | null;
    total_tokens: number;
    items: ModelUsagePoint[];
}

export interface BillingTrendBucket
{
    label: string;
    period_start: string;
    billed_usd: number;
    paid_usd: number;
    provider_cost_usd?: number;
    customer_charge_usd?: number;
    profit_usd?: number;
    payment_total_usd?: number;
    usage_event_count?: number;
    transaction_count: number;
}

export interface BillingTrendResponse
{
    organization_id?: number | null;
    months: number;
    items: BillingTrendBucket[];
}

export interface UsageTrendBucket
{
    label: string;
    period_start: string;
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
}

export interface UsageTrendResponse
{
    organization_id?: number | null;
    months: number;
    items: UsageTrendBucket[];
}

export interface SuperAdminLLMModelPricingTier
{
    label: string;
    sort_order: number;
    max_context_tokens?: number | null;
    input_price_per_million: string;
    output_price_per_million: string;
    cached_input_price_per_million: string;
    image_price_per_million: string;
    cached_pricing_confirmed: boolean;
}

export interface SuperAdminLLMModel
{
    id: number;
    canonical_model_id: string;
    display_name: string;
    provider: string;
    is_selectable: boolean;
    is_active: boolean;
    supports_multimodal: boolean;
    input_modalities: string[];
    output_modalities: string[];
    context_window: number;
    max_output_tokens: number;
    supports_reasoning: boolean;
    reasoning_effort_options: string[];
    reasoning_effort_default?: string | null;
    supports_temperature_mode: 'yes' | 'no' | 'conditional';
    knowledge_cutoff?: string | null;
    notes?: string | null;
    catalog_version: string;
    created_at?: string | null;
    updated_at?: string | null;
    pricing_tiers: SuperAdminLLMModelPricingTier[];
}

export interface SuperAdminLLMModelPricingTierInput
{
    label: string;
    max_context_tokens?: number | null;
    input_price_per_million: number;
    output_price_per_million: number;
    cached_input_price_per_million: number;
    image_price_per_million: number;
    cached_pricing_confirmed: boolean;
}

export interface SuperAdminLLMProviderCapabilityProfile
{
    provider: string;
    input_modalities: string[];
    default_input_modalities: string[];
    output_modalities: string[];
    default_output_modalities: string[];
    supports_reasoning_default: boolean;
    reasoning_effort_options: string[];
    reasoning_effort_default?: string | null;
    temperature_support_options: Array<'yes' | 'no' | 'conditional'>;
    supports_temperature_mode_default: 'yes' | 'no' | 'conditional';
    notes?: string | null;
    source_urls: string[];
}

export interface SuperAdminLLMModelUpsertPayload
{
    canonical_model_id: string;
    display_name: string;
    provider: string;
    is_selectable: boolean;
    is_active: boolean;
    supports_multimodal: boolean;
    input_modalities: string[];
    output_modalities: string[];
    context_window: number;
    max_output_tokens: number;
    supports_reasoning: boolean;
    reasoning_effort_options: string[];
    reasoning_effort_default?: string | null;
    supports_temperature_mode: 'yes' | 'no' | 'conditional';
    knowledge_cutoff?: string | null;
    notes?: string | null;
    pricing_tiers: SuperAdminLLMModelPricingTierInput[];
}

export type OrganizationComparisonMetric = 'tokens' | 'jobs' | 'spend' | 'active_users';

export interface OrganizationComparisonRow
{
    organization_id: number;
    organization_name: string;
    value: number;
    secondary_value?: number | null;
}

export interface OrganizationComparisonResponse
{
    metric: OrganizationComparisonMetric;
    window_days?: number | null;
    items: OrganizationComparisonRow[];
}

export async function getModelUsageDistribution(params: { organization_id?: number; days?: number; limit?: number } = {})
{
    const query: Record<string, number> = {};
    if (params.organization_id) query.organization_id = params.organization_id;
    if (params.days) query.days = params.days;
    if (params.limit) query.limit = params.limit;
    const { data } = await apiClient.get<ModelUsageDistribution>(`${SUPERADMIN_BASE}/stats/model-usage`, {
        params: Object.keys(query).length ? query : undefined,
    });
    return data;
}

export async function getBillingTrend(params: { organization_id?: number; months?: number } = {})
{
    const query: Record<string, number> = {};
    if (params.organization_id) query.organization_id = params.organization_id;
    if (params.months) query.months = params.months;
    const { data } = await apiClient.get<BillingTrendResponse>(`${SUPERADMIN_BASE}/stats/billing-trend`, {
        params: Object.keys(query).length ? query : undefined,
    });
    return data;
}

export async function getUsageTrend(params: { organization_id?: number; months?: number } = {})
{
    const query: Record<string, number> = {};
    if (params.organization_id) query.organization_id = params.organization_id;
    if (params.months) query.months = params.months;
    const { data } = await apiClient.get<UsageTrendResponse>(`${SUPERADMIN_BASE}/stats/usage-trend`, {
        params: Object.keys(query).length ? query : undefined,
    });
    return data;
}

export async function getOrganizationComparison(params: { metric?: OrganizationComparisonMetric; days?: number; limit?: number } = {})
{
    const query: Record<string, number | string> = { metric: params.metric || 'tokens' };
    if (params.days) query.days = params.days;
    if (params.limit) query.limit = params.limit;
    const { data } = await apiClient.get<OrganizationComparisonResponse>(`${SUPERADMIN_BASE}/stats/comparison`, {
        params: query,
    });
    return data;
}

export async function listSuperAdminLLMModels(params: { provider?: string; status?: string; search?: string } = {})
{
    const { data } = await apiClient.get<{ items: SuperAdminLLMModel[]; total: number }>(`${SUPERADMIN_BASE}/llm-models`, {
        params: Object.keys(params).length ? params : undefined,
    });
    return data;
}

export async function listSuperAdminLLMProviderCapabilityProfiles()
{
    const { data } = await apiClient.get<{ items: SuperAdminLLMProviderCapabilityProfile[] }>(`${SUPERADMIN_BASE}/llm-model-provider-profiles`);
    return data;
}

export async function getSuperAdminLLMModel(modelId: number)
{
    const { data } = await apiClient.get<SuperAdminLLMModel>(`${SUPERADMIN_BASE}/llm-models/${modelId}`);
    return data;
}

export async function createSuperAdminLLMModel(payload: SuperAdminLLMModelUpsertPayload)
{
    const { data } = await apiClient.post<SuperAdminLLMModel>(`${SUPERADMIN_BASE}/llm-models`, payload);
    return data;
}

export async function updateSuperAdminLLMModel(modelId: number, payload: SuperAdminLLMModelUpsertPayload)
{
    const { data } = await apiClient.put<SuperAdminLLMModel>(`${SUPERADMIN_BASE}/llm-models/${modelId}`, payload);
    return data;
}

export async function deleteSuperAdminLLMModel(modelId: number)
{
    await apiClient.delete(`${SUPERADMIN_BASE}/llm-models/${modelId}`);
}

// ---------------------------------------------------------------------------
// Org-admin adapters — same chart shapes as superadmin endpoints, but call
// endpoints that are accessible to regular organisation admins.
// ---------------------------------------------------------------------------

/** Model usage distribution for org admins via GET /api/llm-usage/organization */
export async function getOrgAdminModelUsage(days?: number): Promise<ModelUsageDistribution>
{
    const params: Record<string, number> = {};
    if (days) params.days = days;
    const { data } = await apiClient.get<{
        organization_id: number;
        total_tokens: number;
        by_model: Array<{ model: string; prompt_tokens: number; completion_tokens: number; total_tokens: number; job_count: number }>;
    }>('/api/llm-usage/organization', { params: Object.keys(params).length ? params : undefined });
    return {
        organization_id: data.organization_id,
        window_days: days ?? null,
        total_tokens: data.total_tokens,
        items: (data.by_model || []).map((m) => ({
            model: m.model,
            prompt_tokens: m.prompt_tokens,
            completion_tokens: m.completion_tokens,
            total_tokens: m.total_tokens,
            request_count: m.job_count,
        })),
    };
}

/** Billing trend for org admins via GET /api/llm-usage/organization/billing-trend */
export async function getOrgAdminBillingTrend(months = 6): Promise<BillingTrendResponse>
{
    const { data } = await apiClient.get<BillingTrendResponse>('/api/llm-usage/organization/billing-trend', {
        params: { months },
    });
    return data;
}

export async function listOrganizationJobs(orgId: number, page = 1, pageSize = 25, status?: string)
{
    const { data } = await apiClient.get<PagedResponse<SuperAdminJob>>(`${SUPERADMIN_BASE}/jobs/organizations/${orgId}`, {
        params: {
            page,
            page_size: pageSize,
            status,
        },
    });
    return data;
}
